import { useState } from 'react';

import type { Account, AccountChanges, AccountStatusFilter, NewAccount } from '../../api/monitor';
import { ErrorCard, LoadingCard } from '../../components/ui/ScreenFallback';
import { Toast, useToast } from '../../components/ui/Toast';
import { useAccounts } from '../../hooks/useAccounts';
import { accountMatches, accountTitle, usesOtp } from '../../lib/accounts';
import { AccountRow } from './AccountRow';
import { EditAccountModal } from './EditAccountModal';
import { NewAccountModal } from './NewAccountModal';

/** Filtro de situação; "Ativas" primeiro porque é o padrão. */
const STATUS_CHIPS: Array<{ value: AccountStatusFilter; label: string }> = [
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
  { value: 'all', label: 'Todas' },
];

/**
 * Tela de Contas: as `accounts` de cada operadora, com o master dono, quantas
 * linhas cada uma tem e o cadastro de novas.
 *
 * As abas são as operadoras cadastradas, não Claro/Vivo fixos — `operators` é
 * tabela. O que ainda é específico de cada uma são os campos do cadastro, e
 * isso mora em `lib/accounts`.
 *
 * Abre nas contas ativas. "Editar" troca a senha do portal (e o OTP, na Claro)
 * e ativa/inativa a conta — inativa, ela sai das rotinas diárias.
 */
export function AccountsScreen() {
  const controller = useAccounts();
  const { accounts, operators, operator, loading, error, saving } = controller;
  const { toast, show } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Account | null>(null);
  const [query, setQuery] = useState('');

  const visible = accounts.filter((account) => accountMatches(account, query));

  const handleUpdate = async (changes: AccountChanges) => {
    if (!editing) return;

    await controller.update(editing.id, changes);
    setEditing(null);

    const what =
      changes.active === false
        ? 'Conta inativada'
        : changes.active === true
          ? 'Conta reativada'
          : 'Conta atualizada';
    show(`${what}: ${accountTitle(editing)}`);
  };

  const handleCreate = async (payload: NewAccount) => {
    const created = await controller.create(payload);
    setModalOpen(false);
    show(created ? `Conta salva: ${accountTitle(created)}` : 'Conta salva.');
  };

  if (loading && accounts.length === 0 && !error) {
    return <LoadingCard message="Carregando as contas…" />;
  }

  if (error && accounts.length === 0) {
    return (
      <ErrorCard
        title="Não foi possível carregar as contas."
        detail={error}
        onRetry={controller.reload}
      />
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center gap-2.5">
        <div className="bg-surface-300 flex gap-0.5 rounded-[11px] p-[3px]">
          {operators.map((item) => {
            const active = item.code === operator?.code;

            return (
              <button
                key={item.code}
                type="button"
                onClick={() => controller.selectOperator(item.code)}
                aria-pressed={active}
                className={`rounded-[9px] px-[18px] py-2 text-[13px] font-extrabold ${
                  active
                    ? 'text-navy-950 bg-white shadow-[0_1px_3px_rgb(19_31_56/0.12)]'
                    : 'text-ink-600'
                }`}
              >
                {item.name}
              </button>
            );
          })}
        </div>

        <div className="flex gap-1.5" role="group" aria-label="Situação da conta">
          {STATUS_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => controller.setStatus(chip.value)}
              aria-pressed={chip.value === controller.status}
              className={`rounded-full border px-3.5 py-2 text-[12.5px] font-bold ${
                chip.value === controller.status
                  ? 'border-navy-800 bg-navy-800 text-white'
                  : 'border-line-300 text-ink-600 bg-white'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar conta…"
          aria-label="Buscar conta"
          className="border-line-300 focus:border-navy-800 w-60 rounded-[10px] border bg-white px-3.5 py-2.5 text-[14px] outline-none focus:ring-2 focus:ring-[#00244733]"
        />

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          disabled={!operator}
          className="bg-navy-800 hover:bg-navy-950 shrink-0 rounded-[10px] px-4 py-2.5 text-[13px] font-extrabold text-white disabled:opacity-60"
        >
          + Nova conta
        </button>
      </div>

      {operator && usesOtp(operator) ? (
        <div className="mb-3 rounded-xl border border-[#D5E2F8] bg-[#F0F5FF] px-3.5 py-3 text-[12.5px] leading-relaxed text-[#3A4B6B]">
          Contas {operator.name} usam o <b>CPF do titular</b> + senha do portal e uma conta
          Gmail dedicada: o SICOP lê o <b>OTP enviado por e-mail</b> para autenticar no portal.
          O acesso ao Gmail usa um token de app —{' '}
          <a
            href="https://myaccount.google.com/apppasswords"
            target="_blank"
            rel="noreferrer"
            className="font-bold underline"
          >
            gerar token de app do Google →
          </a>
        </div>
      ) : null}

      {error ? (
        <div className="border-danger-200 bg-danger-100 text-danger-600 mb-3 rounded-xl border px-4 py-3 text-[12.5px] font-semibold">
          {error}
        </div>
      ) : null}

      <div
        className={`border-line-200 overflow-hidden rounded-[14px] border bg-white ${
          loading ? 'opacity-60' : ''
        }`}
      >
        {accounts.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nenhuma conta {operator?.name ?? ''}{' '}
            {controller.status === 'active'
              ? 'ativa'
              : controller.status === 'inactive'
                ? 'inativa'
                : 'cadastrada'}
            .
          </div>
        ) : visible.length === 0 ? (
          <div className="text-ink-600 p-7 text-center text-[13.5px]">
            Nenhuma conta encontrada para “{query.trim()}”.
          </div>
        ) : (
          visible.map((account) => (
            <AccountRow key={account.id} account={account} onEdit={setEditing} />
          ))
        )}
      </div>

      {modalOpen && operator ? (
        <NewAccountModal
          operator={operator}
          saving={saving}
          onClose={() => setModalOpen(false)}
          onSubmit={handleCreate}
        />
      ) : null}

      {editing ? (
        <EditAccountModal
          account={editing}
          saving={saving}
          onClose={() => setEditing(null)}
          onSubmit={handleUpdate}
        />
      ) : null}

      <Toast message={toast} />
    </>
  );
}
